interface Tree<T> {
    children?: Tree<T>[];
}

export const traverseTree = <T, Tr extends Tree<T> = Tree<T>>(tree: Tr, callback: (item: Tr) => void) => {
    const children: Tr[] = [tree];

    while (children.length > 0) {
        const item = children.pop()!;

        if (item && item.children && item.children.length > 0) {
            children.push(...(item.children as Tr[]));
        }

        callback(item);
    }
};

export const traverseTrees = <T, Tr extends Tree<T> = Tree<T>>(trees: Tr[], callback: (item: Tr) => void) => {
    trees.forEach((item) => {
        traverseTree(item, callback);
    });
};

export const asyncTraverseTree = async <T>(tree: Tree<T>, callback: (trees: any) => Promise<void>) => {
    const children: Tree<T>[] = [tree];

    while (children.length > 0) {
        const item = children.pop();

        if (item && item.children && item.children.length > 0) {
            children.push(...item.children);
        }

        item && (await callback(item)); // 等待异步处理完成
    }
};

export const asyncTraverseTrees = async <T>(trees: Tree<T>[], callback: (tree: any) => Promise<void>) => {
    for (const item of trees) {
        await asyncTraverseTree(item, callback); // 等待每棵树的遍历完成
    }
};

export const traverseTreePostOrder = <T>(node: Tree<T>, callback: Function) => {
    if (node.children && node.children.length > 0) {
        node.children.forEach((item) => {
            traverseTreePostOrder(item as Tree<T>, callback);
        });

        callback(node);
    }
};

interface Children<T> {
    children?: Tree<T>[];
}
interface Tree<T> extends Children<T> {}

export const traverseTreePreOrder = <T>(root: Tree<T>, callback: Function) => {
    function traverse(node: Children<T>) {
        if (node === null) return;

        callback(node); // 访问节点值
        if (node.children) {
            for (const child of node.children) {
                // 遍历每个孩子并递归
                traverse(child);
            }
        }
    }

    traverse(root);
};

export const removeEmptyChildren = <T>(
    nodes: Tree<T>[],
    excludeLayer: (node: Tree<T>) => boolean = () => false,
): Tree<T>[] => {
    // 非数组输入直接返回（支持空值或单节点输入）
    if (!Array.isArray(nodes)) return [];

    return nodes.reduce((acc, node) => {
        // 创建节点副本，避免修改原始对象
        const nodeCopy = { ...node };

        // 递归处理子节点（若存在）
        if (Array.isArray(nodeCopy.children)) {
            nodeCopy.children = removeEmptyChildren(nodeCopy.children, excludeLayer);
        }

        // 过滤条件：仅保留 children 不存在或存在且非空的节点
        const shouldKeep = excludeLayer(nodeCopy) || !Array.isArray(nodeCopy.children) || nodeCopy.children.length > 0;
        if (shouldKeep) {
            acc.push(nodeCopy);
        }

        return acc;
    }, [] as Tree<T>[]);
};
